import React, { useState } from 'react';
import {
  tabNavContainer,
  tabNavItem,
  pillFilter,
  linkInline,
  linkInlineMuted,
  introCardKeyline,
  FERTILITY_DEMO_SECTION_BORDER,
  demoChapterStackColumnStyle,
} from '../designSystem';

const articles = [
  { id: 1, source: 'NIH / PMC · 2025-06', title: 'Clinical Predictors of IVF Success: What the Data Shows', desc: 'Systematic review of age, AMH, AFC, embryo quality, and modifiable factors. Evidence-based guidance for women and couples.', tags: ['ivf'], badges: ['Evidence-based', 'IVF'], emoji: '🔬', bg: 'linear-gradient(140deg, #c8e8df, #a8d5c8)' },
  { id: 2, source: 'BMC Pregnancy & Childbirth · 2025-08', title: 'Sleep and IVF: What We Learned From 2,000 Women', desc: 'Meta-analysis linking sleep quality to treatment outcomes. Real findings from women in treatment.', tags: ['ivf', 'mental'], badges: ['Evidence-based'], emoji: '😴', bg: 'linear-gradient(140deg, #e6d7eb, #d4c0db)' },
  { id: 3, source: 'Reproductive Endocrinology · 2025-03', title: 'ESHRE 2025 Guideline: Ovarian Stimulation for IVF/ICSI', desc: '46 new evidence-based recommendations on protocols, monitoring, and fertility preservation.', tags: ['ivf', 'meds'], badges: ['Evidence-based', 'Guidelines'], emoji: '📋', bg: 'linear-gradient(140deg, #c8e0f0, #a8c8e0)' },
  { id: 4, source: 'Healthline · 2025-05', title: 'Fertility in Your 30s: What the Research Says', desc: 'Evidence-based look at age, decline, and what you can do. Plus real stories from women who conceived.', tags: ['ivf', 'stories'], badges: ['Evidence-based', 'Real stories'], emoji: '👩', bg: 'linear-gradient(140deg, #f5e8d0, #e8d4b0)' },
  { id: 5, source: 'RESOLVE · 2025-04', title: 'Coping With the Two-Week Wait', desc: 'Mental health strategies, community support, and how women get through the hardest wait. Therapist-backed.', tags: ['mental'], badges: ['Evidence-based', 'Mental health'], emoji: '💙', bg: 'linear-gradient(140deg, #fbeaf0, #f0d8e8)' },
  { id: 6, source: 'Mayo Clinic · 2025-06', title: 'PCOS and Fertility: What Your Doctor Wants You to Know', desc: 'Lifestyle, medications, and IVF success rates for women with PCOS. Practical and evidence-backed.', tags: ['pcos', 'meds'], badges: ['Evidence-based', 'PCOS'], emoji: '🏥', bg: 'linear-gradient(140deg, #d4eaff, #b8d8f8)' },
];

const artTopics = [
  { key: 'all', label: 'All topics' },
  { key: 'ivf', label: 'Understanding IVF' },
  { key: 'mental', label: 'Mental health' },
  { key: 'meds', label: 'Medications' },
  { key: 'pcos', label: 'PCOS' },
  { key: 'stories', label: 'Real stories' },
];

const badgeStyles = {
  'Evidence-based': { bg: '#E1F5EE', color: '#0F6E56', border: 'rgba(29,158,117,0.25)' },
  'Guidelines': { bg: '#E6F1FB', color: '#185FA5', border: 'rgba(24,95,165,0.25)' },
  'Real stories': { bg: '#FBEAF0', color: '#993556', border: 'rgba(212,83,126,0.2)' },
  'Mental health': { bg: '#FBEAF0', color: '#993556', border: 'rgba(212,83,126,0.2)' },
  'IVF': { bg: '#EEEDFE', color: '#534AB7', border: 'rgba(127,119,221,0.25)' },
  'PCOS': { bg: '#EEEDFE', color: '#534AB7', border: 'rgba(127,119,221,0.25)' },
};

const checklists = [
  { id: 'retrieval', title: 'Egg retrieval prep checklist', desc: 'Everything to do the night before and morning of your retrieval.', items: ['Stop eating and drinking by midnight the night before', 'Confirm trigger shot time with your clinic', 'Arrange someone to drive you home — you cannot drive after sedation', 'Remove nail polish (for pulse oximeter accuracy)', 'Leave jewelry and valuables at home', 'Wear comfortable, loose clothing', 'Bring your ID and insurance card', 'Plan for 1–2 days of rest at home after the procedure'] },
  { id: 'monitoring', title: 'Monitoring scan — questions to ask', desc: 'Write these down or screenshot before your scan appointment.', items: ['How many follicles are growing and what sizes?', 'What is my estrogen (E2) level today?', 'When is my estimated retrieval date?', 'Should I change my medication doses?', 'When is my next monitoring scan?', 'Are there any signs of OHSS I should watch for?'] },
  { id: 'transfer', title: 'Transfer day checklist', desc: 'What to prepare and bring on your embryo transfer day.', items: ['Take your progesterone and estrogen as scheduled', 'Drink water to fill your bladder (if clinic requires)', 'Wear comfortable, loose clothing — avoid perfumes', 'Bring socks — the transfer table can be cold', 'Ask which embryo(s) are being transferred and the grade', 'Confirm your beta HCG test date before leaving', 'Plan a rest day — no heavy exercise or lifting for 24 hours'] },
];

const glossaryData = [
  { a: 'IVF', f: 'In Vitro Fertilization', d: 'Eggs retrieved from ovaries, fertilized in a lab, embryos transferred to uterus.', c: 'med' },
  { a: 'IUI', f: 'Intrauterine Insemination', d: 'Sperm placed directly into the uterus around ovulation. Less invasive than IVF.', c: 'med' },
  { a: 'AMH', f: 'Anti-Müllerian Hormone', d: 'Key marker of ovarian reserve — how many eggs you likely have left.', c: 'med' },
  { a: 'FSH', f: 'Follicle-Stimulating Hormone', d: 'Stimulates egg development. High FSH can indicate lower ovarian reserve.', c: 'med' },
  { a: 'LH', f: 'Luteinizing Hormone', d: 'Triggers ovulation. LH surge is what OPKs detect.', c: 'med' },
  { a: 'E2', f: 'Estradiol (Estrogen)', d: 'Estrogen level checked via bloodwork during monitoring.', c: 'med' },
  { a: 'P4', f: 'Progesterone', d: 'Hormone supporting uterine lining after ovulation or transfer.', c: 'med' },
  { a: 'AFC', f: 'Antral Follicle Count', d: 'Ultrasound count of resting follicles — indicates ovarian reserve.', c: 'med' },
  { a: 'DOR', f: 'Diminished Ovarian Reserve', d: 'Fewer eggs than expected for age. Often low AMH or AFC.', c: 'med' },
  { a: 'POI', f: 'Premature Ovarian Insufficiency', d: 'Ovaries stop working normally before age 40.', c: 'med' },
  { a: 'PCOS', f: 'Polycystic Ovary Syndrome', d: 'Hormonal condition affecting ovulation, often involving cysts.', c: 'med' },
  { a: 'OHSS', f: 'Ovarian Hyperstimulation Syndrome', d: 'Ovaries overrespond to stim meds — bloating, pain, rarely hospitalization.', c: 'med' },
  { a: 'MFI', f: 'Male Factor Infertility', d: 'Infertility caused by sperm issues — count, motility, or morphology.', c: 'med' },
  { a: 'SA', f: 'Semen Analysis', d: 'Lab test measuring sperm count, motility, and shape.', c: 'med' },
  { a: 'RPL', f: 'Recurrent Pregnancy Loss', d: 'Two or more pregnancy losses. May prompt additional testing.', c: 'med' },
  { a: 'CP', f: 'Chemical Pregnancy', d: 'Very early loss — positive HCG but miscarriage before ultrasound.', c: 'med' },
  { a: 'EP', f: 'Ectopic Pregnancy', d: 'Embryo implants outside the uterus, usually in a fallopian tube.', c: 'med' },
  { a: 'HCG', f: 'Human Chorionic Gonadotropin', d: 'Pregnancy hormone. Rising HCG = good sign after transfer.', c: 'med' },
  { a: 'ERA', f: 'Endometrial Receptivity Analysis', d: 'Test finding the best window for embryo transfer.', c: 'med' },
  { a: 'HSG', f: 'Hysterosalpingogram', d: 'X-ray checking if fallopian tubes are open.', c: 'med' },
  { a: 'CD', f: 'Cycle Day', d: 'Day of your menstrual cycle. CD1 = first day of full flow.', c: 'med' },
  { a: 'OPK', f: 'Ovulation Predictor Kit', d: 'Test strips detecting the LH surge before ovulation.', c: 'med' },
  { a: 'ET', f: 'Embryo Transfer', d: 'Procedure where a fertilized embryo is placed into the uterus.', c: 'treat' },
  { a: 'FET', f: 'Frozen Embryo Transfer', d: 'Transfer using a frozen embryo from a previous retrieval.', c: 'treat' },
  { a: 'TI', f: 'Timed Intercourse', d: 'Doctor-guided intercourse scheduled around ovulation.', c: 'treat' },
  { a: 'US', f: 'Ultrasound', d: 'Monitoring scan checking follicle size, lining, and ovarian response.', c: 'treat' },
  { a: 'SHG', f: 'Sonohysterogram', d: 'Ultrasound with saline to check inside the uterus.', c: 'treat' },
  { a: 'ER', f: 'Egg Retrieval', d: 'Procedure where mature eggs are removed from follicles under sedation.', c: 'treat' },
  { a: 'ICSI', f: 'Intracytoplasmic Sperm Injection', d: 'Single sperm injected directly into egg. Used with male factor infertility.', c: 'treat' },
  { a: 'PGT', f: 'Preimplantation Genetic Testing', d: 'Genetic testing of embryos before transfer.', c: 'treat' },
  { a: 'PGT-A', f: 'PGT for Aneuploidy', d: 'Tests embryos for correct chromosome count. Euploid = normal.', c: 'treat' },
  { a: 'PGT-M', f: 'PGT for Monogenic Disease', d: 'Tests for specific single-gene disorders like BRCA.', c: 'treat' },
  { a: 'AH', f: 'Assisted Hatching', d: 'Creates a small opening in the embryo shell to aid implantation.', c: 'treat' },
  { a: 'OE', f: 'Own Eggs', d: 'Using your own eggs for IVF, as opposed to donor eggs.', c: 'treat' },
  { a: 'DE', f: 'Donor Eggs', d: 'Eggs from another woman used when someone cannot use their own.', c: 'treat' },
  { a: 'DS', f: 'Donor Sperm', d: "Sperm from a donor when a partner's sperm is unavailable.", c: 'treat' },
  { a: 'GS', f: 'Gestational Surrogate', d: "Person who carries a pregnancy using another person's embryo.", c: 'treat' },
  { a: 'GC', f: 'Gestational Carrier', d: "Person who carries a pregnancy using another person's embryo.", c: 'treat' },
  { a: 'Mock ET', f: 'Mock Embryo Transfer', d: 'Trial run of the transfer procedure.', c: 'treat' },
  { a: 'D&C', f: 'Dilation & Curettage', d: 'Procedure to remove tissue from the uterus after miscarriage.', c: 'treat' },
  { a: 'LAP', f: 'Laparoscopy', d: 'Minimally invasive surgery to diagnose/treat endometriosis.', c: 'treat' },
  { a: 'TESA', f: 'Testicular Sperm Aspiration', d: 'Retrieves sperm from the testicle for use in ICSI.', c: 'treat' },
  { a: 'EFT', f: 'Elective Frozen Transfer', d: 'Choosing to freeze all embryos and transfer in a later cycle.', c: 'treat' },
  { a: 'BW', f: 'Bloodwork', d: 'Blood tests during monitoring — E2, LH, P4, and HCG.', c: 'labs' },
  { a: 'Beta', f: 'Beta HCG Test', d: 'Blood test 10–14 days after transfer confirming pregnancy.', c: 'labs' },
  { a: 'TSH', f: 'Thyroid-Stimulating Hormone', d: 'Thyroid function test. Optimal levels matter for fertility.', c: 'labs' },
  { a: 'NK', f: 'Natural Killer Cells', d: 'Immune cells tested in recurrent implantation failure.', c: 'labs' },
  { a: 'RIF', f: 'Recurrent Implantation Failure', d: 'Multiple failed transfers with good-quality embryos.', c: 'labs' },
  { a: 'RPL Panel', f: 'Recurrent Pregnancy Loss Panel', d: 'Blood tests for clotting, immune, and genetic factors.', c: 'labs' },
  { a: 'Karyotype', f: 'Chromosome Analysis', d: 'Checks if either partner has chromosomal abnormalities.', c: 'labs' },
  { a: 'EMMA', f: 'Endometrial Microbiome Analysis', d: 'Tests bacteria in the uterus. Done alongside ERA.', c: 'labs' },
  { a: 'ALICE', f: 'Analysis of Infectious Chronic Endometritis', d: 'Tests for bacterial infection in the uterine lining.', c: 'labs' },
  { a: 'DFI', f: 'DNA Fragmentation Index', d: 'Tests sperm DNA quality.', c: 'labs' },
  { a: 'CF Screen', f: 'Cystic Fibrosis Carrier Screen', d: 'Genetic carrier test done as part of fertility workup.', c: 'labs' },
  { a: 'Stims', f: 'Stimulation Medications', d: 'Injectable gonadotropins stimulating multiple follicles.', c: 'meds' },
  { a: 'GnRH-a', f: 'GnRH Agonist', d: 'Medications like Lupron suppressing natural hormones.', c: 'meds' },
  { a: 'GnRH-ant', f: 'GnRH Antagonist', d: 'Medications like Cetrotide preventing premature ovulation.', c: 'meds' },
  { a: 'rFSH', f: 'Recombinant FSH', d: 'Synthetic FSH for stimulation. Includes Gonal-F and Follistim.', c: 'meds' },
  { a: 'hMG', f: 'Human Menopausal Gonadotropin', d: 'Contains FSH + LH. Includes Menopur.', c: 'meds' },
  { a: 'HCG trigger', f: 'HCG Trigger Shot', d: 'Final injection 34–36 hours before retrieval.', c: 'meds' },
  { a: 'Lupron trigger', f: 'Leuprolide Acetate Trigger', d: 'Alternative trigger — lower OHSS risk.', c: 'meds' },
  { a: 'PIO', f: 'Progesterone in Oil', d: 'Intramuscular progesterone injection after transfer.', c: 'meds' },
  { a: 'DHEA', f: 'Dehydroepiandrosterone', d: 'Supplement some clinics recommend for DOR.', c: 'meds' },
  { a: 'CoQ10', f: 'Coenzyme Q10', d: 'Antioxidant supporting egg and sperm quality.', c: 'meds' },
  { a: 'IVIG', f: 'Intravenous Immunoglobulin', d: 'Immune treatment used in RIF or RPL protocols.', c: 'meds' },
  { a: 'Estrace', f: 'Estradiol', d: 'Estrogen medication building uterine lining in FET cycles.', c: 'meds' },
  { a: 'Crinone', f: 'Progesterone Gel', d: 'Vaginal progesterone alternative to PIO.', c: 'meds' },
  { a: 'Menopur', f: 'Menotropins', d: 'Stim medication with FSH + LH. Often combined with Gonal-F.', c: 'meds' },
  { a: 'Letrozole', f: 'Aromatase Inhibitor', d: 'Oral medication stimulating ovulation, used in PCOS.', c: 'meds' },
  { a: 'Clomid', f: 'Clomiphene Citrate', d: 'Oral fertility medication. Common first-line treatment.', c: 'meds' },
  { a: 'MTX', f: 'Methotrexate', d: 'Medication used to treat ectopic pregnancies.', c: 'meds' },
  { a: 'TWW', f: 'Two-Week Wait', d: 'The wait between transfer or ovulation and your pregnancy test.', c: 'community' },
  { a: 'BFP', f: 'Big Fat Positive', d: 'A positive pregnancy test. The moment everyone is waiting for.', c: 'community' },
  { a: 'BFN', f: 'Big Fat Negative', d: 'A negative pregnancy test. You are not alone.', c: 'community' },
  { a: 'PUPO', f: 'Pregnant Until Proven Otherwise', d: 'Treating yourself as pregnant while waiting for beta.', c: 'community' },
  { a: 'TTC', f: 'Trying to Conceive', d: 'Actively working toward pregnancy.', c: 'community' },
  { a: 'AF', f: 'Aunt Flo (Period)', d: '"AF arrived" = cycle did not work.', c: 'community' },
  { a: 'DPO', f: 'Days Past Ovulation', d: 'How many days since ovulation during the TWW.', c: 'community' },
  { a: 'DP5DT', f: 'Days Post 5-Day Transfer', d: 'Days since a day-5 blastocyst transfer.', c: 'community' },
  { a: 'POAS', f: 'Pee On A Stick', d: 'Taking a home pregnancy test during the TWW.', c: 'community' },
  { a: 'HPT', f: 'Home Pregnancy Test', d: 'Over-the-counter pregnancy test. Less accurate than beta.', c: 'community' },
  { a: 'MC', f: 'Miscarriage', d: 'Pregnancy loss. Common in the fertility community.', c: 'community' },
  { a: 'UI', f: 'Unexplained Infertility', d: 'No identifiable cause found. More common than you think.', c: 'community' },
  { a: 'PAL', f: 'Pregnancy After Loss', d: 'Being pregnant after a previous miscarriage.', c: 'community' },
  { a: 'RAJ', f: 'Raging At Jealousy', d: 'The difficult feeling when others get pregnant easily.', c: 'community' },
  { a: 'Sticky', f: 'Sticky Vibes', d: 'Wishing for a pregnancy that sticks and continues.', c: 'community' },
  { a: 'Embies', f: 'Embryos', d: 'Affectionate community term. Very common post-retrieval.', c: 'community' },
  { a: 'Follies', f: 'Follicles', d: '"6 follies at scan" = 6 follicles measured.', c: 'community' },
  { a: 'OH', f: 'Other Half / Partner', d: 'Community shorthand for your partner or spouse.', c: 'community' },
  { a: 'RE', f: 'Reproductive Endocrinologist', d: 'Your fertility specialist.', c: 'community' },
  { a: 'NP', f: 'Nurse Practitioner', d: 'Your day-to-day clinic contact for monitoring results.', c: 'community' },
  { a: 'REI', f: 'Reproductive Endocrinology & Infertility', d: 'The medical specialty. Your fertility doctor is an REI.', c: 'providers' },
  { a: 'OB-GYN', f: 'Obstetrician-Gynecologist', d: 'Your regular doctor. Often refers to an REI.', c: 'providers' },
  { a: 'MFM', f: 'Maternal-Fetal Medicine', d: 'High-risk pregnancy specialist. May be involved post-IVF.', c: 'providers' },
  { a: 'CCRM', f: 'Colorado Center for Reproductive Medicine', d: 'One of the most well-known fertility clinic networks in the US.', c: 'providers' },
  { a: 'SART', f: 'Society for Assisted Reproductive Technology', d: 'Publishes IVF success rates by clinic.', c: 'providers' },
  { a: 'ASRM', f: 'American Society for Reproductive Medicine', d: 'Sets guidelines for fertility treatment.', c: 'providers' },
  { a: 'RESOLVE', f: 'National Infertility Association', d: 'Largest nonprofit supporting people experiencing infertility.', c: 'providers' },
  { a: 'PCP', f: 'Primary Care Provider', d: 'Your general doctor — involved in referrals.', c: 'providers' },
  { a: 'LAc', f: 'Licensed Acupuncturist', d: 'Many women use acupuncture alongside IVF.', c: 'providers' },
  { a: 'RD', f: 'Registered Dietitian', d: 'Consulted for diet optimization during treatment.', c: 'providers' },
  { a: 'RDN', f: 'Registered Dietitian Nutritionist', d: 'Consulted for diet optimization during treatment.', c: 'providers' },
  { a: 'CNM', f: 'Certified Nurse Midwife', d: 'May be involved once care transfers from REI to OB.', c: 'providers' },
  { a: 'NFIB', f: 'National Fertility Insurance Benchmark', d: 'Used when discussing fertility coverage in employer plans.', c: 'providers' },
  { a: 'BMI', f: 'Body Mass Index', d: 'Clinics often have BMI requirements for IVF cycles.', c: 'med' },
];

const gCategories = [
  { key: 'all', label: null },
  { key: 'med', label: 'Medical', color: '#D4537E' },
  { key: 'treat', label: 'Treatments', color: '#185FA5' },
  { key: 'labs', label: 'Labs', color: '#7F77DD' },
  { key: 'meds', label: 'Medications', color: '#1D9E75' },
  { key: 'community', label: 'Community', color: '#BE185D' },
  { key: 'providers', label: 'Providers', color: '#D85A30' },
];

const gAccentColors = { med: '#D4537E', treat: '#185FA5', labs: '#7F77DD', meds: '#1D9E75', community: '#BE185D', providers: '#D85A30' };

const gLegendLabels = { med: 'Medical & Clinical', treat: 'Treatments', labs: 'Labs & Testing', meds: 'Medications', community: 'Community & TWW', providers: 'Providers' };

function highlightText(text, q) {
  if (!q || !text) return text;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  return text.replace(regex, (m) => `<mark style="background:rgba(212,83,126,0.2);padding:0 1px;border-radius:2px">${m}</mark>`);
}

export default function FertilityDemoResources({ navigate, omitHeader = false, demoChapter = 'fertility' }) {
  const sectionBorder = demoChapter === 'fertility' ? FERTILITY_DEMO_SECTION_BORDER : '0.5px solid rgba(212,83,126,0.12)';
  const [activeTab, setActiveTab] = useState('articles');
  const [artFilter, setArtFilter] = useState('all');
  const [gFilter, setGFilter] = useState('all');
  const [gSearch, setGSearch] = useState('');
  const [savedArticles, setSavedArticles] = useState({});
  const [checkedItems, setCheckedItems] = useState({});

  const filteredArticles = artFilter === 'all' ? articles : articles.filter((a) => a.tags.includes(artFilter));

  const filteredGlossary = glossaryData.filter((item) => {
    const matchFilter = gFilter === 'all' || item.c === gFilter;
    if (!matchFilter) return false;
    if (!gSearch.trim()) return true;
    const q = gSearch.toLowerCase();
    return item.a.toLowerCase().includes(q) || item.f.toLowerCase().includes(q) || item.d.toLowerCase().includes(q);
  });

  const tabs = [
    { key: 'articles', label: 'Articles & guides' },
    { key: 'glossary', label: 'Acronym glossary' },
    { key: 'checklists', label: 'Checklists' },
    { key: 'saved', label: 'Saved' },
  ];

  return (
    <div style={demoChapterStackColumnStyle}>
      {!omitHeader && (
        <div style={{ background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', ...introCardKeyline, padding: '22px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div className="page-eyebrow">
              <span aria-hidden>🌸</span>
              <span>Fertility · Resources</span>
            </div>
            <button
              type="button"
              onClick={() => navigate?.('/signup?source=demo-fertility-resources')}
              style={{
                background: 'white',
                color: '#993556',
                border: 'none',
                borderRadius: 999,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Join the waitlist
            </button>
          </div>
          <h1 className="page-title">Resources</h1>
          <p className="page-descriptor">Evidence-based articles, the fertility glossary, retrieval prep checklists, and your saved items.</p>
        </div>
      )}

      {/* 2. Tab bar — TYPE 3 */}
      <div style={{ ...tabNavContainer, marginBottom: 14, overflowX: 'auto' }}>
        {tabs.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} style={tabNavItem(activeTab === t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* 3A. Articles tab */}
      {activeTab === 'articles' && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {artTopics.map((t) => (
              <button key={t.key} type="button" onClick={() => setArtFilter(t.key)} style={pillFilter(artFilter === t.key)}>{t.label}</button>
            ))}
          </div>
          <div style={{ background: 'var(--white)', border: sectionBorder, borderRadius: 14, overflow: 'hidden' }}>
            {filteredArticles.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  gap: 14,
                  padding: '14px 16px',
                  alignItems: 'flex-start',
                  borderBottom: i < filteredArticles.length - 1 ? '0.5px solid rgba(212,83,126,0.08)' : 'none',
                }}
              >
                <div style={{ width: 110, height: 78, borderRadius: 8, flexShrink: 0, background: a.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{a.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)', marginBottom: 4 }}>{a.source}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22', marginBottom: 4, lineHeight: 1.4 }}>{a.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)', marginBottom: 8, lineHeight: 1.5 }}>{a.desc}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {a.badges.map((b) => {
                        const s = badgeStyles[b] || { bg: '#f0f0f0', color: '#666', border: '#ddd' };
                        return <span key={b} style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 500, border: `0.5px solid ${s.border}`, background: s.bg, color: s.color }}>{b}</span>;
                      })}
                    </div>
                    <span
                      onClick={() => setSavedArticles((prev) => (prev[a.id] ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== String(a.id))) : { ...prev, [a.id]: a.title }))}
                      role="button"
                      tabIndex={0}
                      style={{ ...linkInline, color: savedArticles[a.id] ? '#1D9E75' : undefined }}
                    >
                      {savedArticles[a.id] ? '✓ Saved' : 'Save'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 3B. Glossary tab */}
      {activeTab === 'glossary' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {gCategories.filter((c) => c.key !== 'all').map((c) => (
              <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 7, height: 7, borderRadius: 2, background: gAccentColors[c.key] }} />
                <span style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>{gLegendLabels[c.key] || c.label}</span>
              </div>
            ))}
          </div>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'rgba(212,83,126,0.45)', pointerEvents: 'none' }}>🔍</span>
            <input
              type="text"
              value={gSearch}
              onChange={(e) => setGSearch(e.target.value)}
              placeholder="Search — try IVF, TWW, AMH, PUPO, OHSS..."
              style={{ width: '100%', fontSize: 11, background: 'var(--color-background-secondary, #f5f5f5)', border: '0.5px solid rgba(212,83,126,0.2)', borderRadius: 8, padding: '9px 36px 9px 34px', outline: 'none', boxSizing: 'border-box' }}
            />
            {gSearch.length > 0 && (
              <span onClick={() => setGSearch('')} role="button" tabIndex={0} style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'var(--color-text-tertiary, #888)', cursor: 'pointer' }}>✕</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {gCategories.map((c) => (
              <button key={c.key} type="button" onClick={() => setGFilter(c.key)} style={pillFilter(gFilter === c.key)}>
                {c.key === 'all' ? `All (${glossaryData.length})` : c.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)', marginBottom: 10 }}>
            {gSearch ? `${filteredGlossary.length} result${filteredGlossary.length !== 1 ? 's' : ''} for '${gSearch}'` : `Showing ${gFilter === 'all' ? `all ${glossaryData.length}` : filteredGlossary.length} acronyms`}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
            {filteredGlossary.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 24, fontSize: 12, color: 'var(--color-text-tertiary, #888)' }}>No acronyms found — try a different term</div>
            ) : (
              filteredGlossary.map((item) => (
                <div key={item.a} style={{ background: 'var(--color-background-secondary, #f8f8f8)', borderRadius: 8, padding: '10px 12px', borderLeft: `3px solid ${gAccentColors[item.c] || '#999'}` }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: gAccentColors[item.c] || '#2d1a22' }}>{item.a}</div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: '#2d1a22', marginBottom: 2 }} dangerouslySetInnerHTML={{ __html: highlightText(item.f, gSearch) }} />
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: highlightText(item.d, gSearch) }} />
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* 3C. Checklists tab */}
      {activeTab === 'checklists' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginBottom: 14, lineHeight: 1.6 }}>Tap each item to check it off as you go. Your progress saves automatically.</div>
          {checklists.map((cl) => {
            const itemKeys = cl.items.map((_, i) => `${cl.id}-${i}`);
            const doneCount = itemKeys.filter((k) => checkedItems[k]).length;
            const totalCount = cl.items.length;
            return (
              <div key={cl.id} style={{ border: sectionBorder, borderRadius: 12, padding: '14px 16px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22' }}>{cl.title}</div>
                  <div style={{ fontSize: 10, color: '#D4537E', fontWeight: 500 }}>{doneCount} / {totalCount}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)', marginBottom: 8, lineHeight: 1.5 }}>{cl.desc}</div>
                <div style={{ height: 4, background: 'rgba(212,83,126,0.1)', borderRadius: 999, marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: '#D4537E', borderRadius: 999, width: `${Math.round((doneCount / totalCount) * 100)}%`, transition: 'width 0.3s' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {cl.items.map((item, idx) => {
                    const itemKey = `${cl.id}-${idx}`;
                    const isChecked = !!checkedItems[itemKey];
                    return (
                      <div key={itemKey} onClick={() => setCheckedItems((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }))} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} role="button" tabIndex={0}>
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            background: isChecked ? '#1D9E75' : 'transparent',
                            border: isChecked ? 'none' : '1px solid rgba(212,83,126,0.3)',
                            color: 'white',
                          }}
                        >
                          {isChecked ? '✓' : ''}
                        </div>
                        <span style={{ fontSize: 11, color: isChecked ? 'var(--color-text-tertiary, #888)' : '#2d1a22', textDecoration: isChecked ? 'line-through' : 'none' }}>{item}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* 3D. Saved tab */}
      {activeTab === 'saved' && (
        <>
          {Object.entries(savedArticles).length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>🔖</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#2d1a22', marginBottom: 6 }}>No saved items yet</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)' }}>Tap Save on any article to bookmark it here</div>
            </div>
          ) : (
            <div style={{ background: 'var(--white)', border: sectionBorder, borderRadius: 14, overflow: 'hidden' }}>
              {Object.entries(savedArticles).map(([id, title], i, arr) => (
                <div
                  key={id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '12px 14px',
                    alignItems: 'flex-start',
                    borderBottom: i < arr.length - 1 ? '0.5px solid rgba(212,83,126,0.08)' : 'none',
                  }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#FBEAF0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>📄</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#2d1a22', marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>Saved article</div>
                  </div>
                  <span onClick={() => setSavedArticles((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id)))} role="button" tabIndex={0} style={linkInlineMuted}>Remove</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
